import React, { useEffect, useMemo, useState } from 'react';
import {
  AnalysisData, Metric, RoundFilter, buildModel, highlights, isCompetitor, missingData,
} from '../../utils/majorAnalysis';
import { Standing } from '../../utils/majorLeaderboard';
import { seriesColor } from '../../utils/chartPalette';
import DataNeeded from './DataNeeded';
import PositionTrend from './PositionTrend';
import CoursePerformance from './CoursePerformance';
import HoleByHole from './HoleByHole';
import ScoringMix from './ScoringMix';
import Putting from './Putting';
import Penalties from './Penalties';
import { Seg, rankText } from './shared';
import '../../css/majorAnalysis.css';

export interface MajorAnalysisProps {
  tournamentId: number;
  /** The leaderboard's finishing order (net) — the default order everywhere. */
  standings: Standing[];
  /** Scheduled majors: each player's released round_ids in personal-round order. */
  roundOrder: Map<number, number[]> | null;
  getAccessTokenSilently: () => Promise<string>;
  /** Opens the round's By Hole editor (scores, putts, penalties, scorecard photo). */
  onOpenRound: (roundId: number) => void;
  /** Bumped after the By Hole editor saves, so the analysis reloads. */
  reloadKey?: number;
  /** e.g. "2 of 3 rounds released" while a major is still being played. */
  releasedNote?: string | null;
}

type Tab = 'trend' | 'courses' | 'holes' | 'scoring' | 'putting' | 'penalties';
const TABS: { key: Tab; label: string }[] = [
  { key: 'trend', label: 'Position trend' },
  { key: 'courses', label: 'Courses' },
  { key: 'holes', label: 'Hole by hole' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'putting', label: 'Putting' },
  { key: 'penalties', label: 'Penalties' },
];

// Which global filters a tab uses; the rest are shown disabled with why.
const USES: Record<Tab, { round: boolean; course: boolean; metric: boolean; why: string }> = {
  trend: { round: false, course: false, metric: true, why: 'The position trend always covers the whole major.' },
  courses: { round: true, course: false, metric: true, why: 'Courses compares every course side by side.' },
  holes: { round: true, course: false, metric: true, why: 'Pick the course above the hole grid.' },
  scoring: { round: true, course: true, metric: false, why: 'Birdies, pars and bogeys are always gross.' },
  putting: { round: true, course: true, metric: false, why: 'Putting stats are always gross.' },
  penalties: { round: true, course: true, metric: false, why: 'Penalty strokes don’t depend on net or gross.' },
};

const MajorAnalysis: React.FC<MajorAnalysisProps> = ({
  tournamentId, standings, roundOrder, getAccessTokenSilently, onOpenRound, reloadKey = 0, releasedNote,
}) => {
  const [data, setData] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [tab, setTab] = useState<Tab>('trend');
  const [metric, setMetric] = useState<Metric>('net');
  const [round, setRound] = useState<'all' | number>('all');
  const [course, setCourse] = useState<'all' | string>('all');
  const [includeGuests, setIncludeGuests] = useState(false);
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const [neededOpen, setNeededOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let stale = false;
    (async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/tournaments/${tournamentId}/analysis`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `HTTP ${res.status}`);
        const json = await res.json();
        if (!stale) { setData(json); setError(null); }
      } catch (err) {
        if (!stale) setError(err instanceof Error ? err.message : 'Failed to load the analysis.');
      } finally {
        if (!stale) setLoading(false);
      }
    })();
    return () => { stale = true; };
  }, [tournamentId, reloadKey, attempt, getAccessTokenSilently]);

  const model = useMemo(() => (data ? buildModel(data, standings, { roundOrder }) : null), [data, standings, roundOrder]);
  const missing = useMemo(() => (model ? missingData(model) : null), [model]);
  const tiles = useMemo(() => (model ? highlights(model) : []), [model]);

  if (!model || !missing) {
    return (
      <div className="ma-root">
        {error
          ? <div className="ma-empty"><div className="ma-empty-title">Couldn’t load the analysis: {error}</div><button type="button" className="ma-btn" onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>
          : <div className="ma-empty"><div className="ma-empty-title">{loading ? 'Loading the analysis…' : 'No data.'}</div></div>}
      </div>
    );
  }

  if (!model.players.length) {
    return (
      <div className="ma-root">
        <div className="ma-empty"><div className="ma-empty-title">No released scores for this major yet — the analysis appears once a round is complete for everyone.</div></div>
      </div>
    );
  }

  const uses = USES[tab];
  const filter: RoundFilter = { round: uses.round ? round : 'all', course: uses.course ? course : 'all' };
  const effectiveMetric: Metric = uses.metric ? metric : 'gross';
  const guestsExist = model.players.some((p) => !isCompetitor(p));
  const chipPlayers = model.players.filter((p) => includeGuests || isCompetitor(p));
  const shown = chipPlayers.filter((p) => !hidden.has(p.player_id));
  const visible = new Set(shown.map((p) => p.player_id));
  const competitors = model.players.filter(isCompetitor);
  const showTop = (n: number) => setHidden(new Set(competitors.slice(n).map((p) => p.player_id)));
  const toggleHidden = (id: number) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const topN = (n: number) => competitors.length > n && competitors.every((p, i) => (i < n) !== hidden.has(p.player_id));
  const open = neededOpen ?? missing.anything;

  return (
    <div className={`ma-root${loading ? ' is-refreshing' : ''}`}>
      {tiles.length > 0 && (
        <div className="ma-tiles" aria-label="Highlights">
          {tiles.map((t) => (
            <div key={t.key} role="group" aria-label={t.label} className={`ma-tile${t.key === 'winner' ? ' ma-tile--gold' : ''}`}>
              <div className="ma-tile-label">{t.label}</div>
              <div className="ma-tile-value">{t.value}</div>
              {t.detail && <div className="ma-tile-detail">{t.detail}</div>}
            </div>
          ))}
        </div>
      )}
      {releasedNote && <div className="ma-note">{releasedNote}</div>}

      <DataNeeded missing={missing} onOpenRound={onOpenRound} open={open} onToggle={() => setNeededOpen(!open)} />

      <div className="ma-filters" aria-label="Filters">
        <div className="ma-filter-players">
          <span className="ma-filter-label">Players</span>
          <div className="ma-presets">
            <button type="button" className={hidden.size === 0 ? 'active' : ''} onClick={() => setHidden(new Set())}>All</button>
            {competitors.length > 3 && <button type="button" className={topN(3) ? 'active' : ''} onClick={() => showTop(3)}>Top 3</button>}
            {competitors.length > 5 && <button type="button" className={topN(5) ? 'active' : ''} onClick={() => showTop(5)}>Top 5</button>}
          </div>
          <div className="ma-chips">
            {chipPlayers.map((p) => (
              <button key={p.player_id} type="button" className={`ma-chip${hidden.has(p.player_id) ? ' is-off' : ''}`}
                aria-pressed={!hidden.has(p.player_id)} onClick={() => toggleHidden(p.player_id)}
                title={hidden.has(p.player_id) ? `Show ${p.name}` : `Hide ${p.name}`}>
                <span className="ma-swatch" style={{ background: seriesColor(p.order) }} />
                <span className="ma-chip-rank">{rankText(p, model.players)}</span>
                {p.first_name}
              </button>
            ))}
          </div>
        </div>
        <div className="ma-filter-row">
          <Seg label="Round" value={round} onChange={setRound} disabled={!uses.round} title={!uses.round ? uses.why : undefined}
            options={[{ value: 'all' as const, label: 'All rounds' }, ...Array.from({ length: model.maxRounds }, (_, i) => ({ value: i + 1, label: `R${i + 1}` }))]} />
          <label className={`ma-field${!uses.course ? ' is-disabled' : ''}`} title={!uses.course ? uses.why : undefined}>
            <span>Course</span>
            <select value={course} disabled={!uses.course} onChange={(e) => setCourse(e.target.value)}>
              <option value="all">All courses</option>
              {model.courses.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <Seg label="Net or gross" value={effectiveMetric} onChange={setMetric} disabled={!uses.metric} title={!uses.metric ? uses.why : undefined}
            options={[{ value: 'net' as Metric, label: 'Net' }, { value: 'gross' as Metric, label: 'Gross' }]} />
          {guestsExist && (
            <label className="ma-check">
              <input type="checkbox" checked={includeGuests} onChange={(e) => setIncludeGuests(e.target.checked)} /> Guests
            </label>
          )}
        </div>
        <p className="ma-filter-note">Filters change what’s shown — field averages and places always use the whole field.</p>
      </div>

      <div className="ma-tabs" role="tablist" aria-label="Analysis">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label}>
        {tab === 'trend' && <PositionTrend model={model} visible={visible} metric={effectiveMetric} missing={missing} onOpenRound={onOpenRound} />}
        {tab === 'courses' && <CoursePerformance model={model} players={shown} metric={effectiveMetric} filter={filter} />}
        {tab === 'holes' && <HoleByHole key={course} model={model} players={shown} metric={effectiveMetric} filter={{ round: filter.round, course }} missing={missing} onOpenRound={onOpenRound} />}
        {tab === 'scoring' && <ScoringMix model={model} players={shown} filter={filter} missing={missing} onOpenRound={onOpenRound} />}
        {tab === 'putting' && <Putting model={model} players={shown} filter={filter} missing={missing} onOpenRound={onOpenRound} />}
        {tab === 'penalties' && <Penalties model={model} players={shown} filter={filter} missing={missing} onOpenRound={onOpenRound} />}
      </div>
    </div>
  );
};

export default MajorAnalysis;
